import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import IAddress from "../../entities/IAddress";
import AddressService from "../../services/AddressService";
import { getErrorMessage } from "../../services/apiUtils";

type AddressFormDialogProps = {
  open: boolean;
  userId: number | null;
  address?: IAddress | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

interface AddressFormValues {
  address1: string;
  cityName: string;
  stateName: string;
  district: string;
  postCode: string;
  phone: string;
}

const emptyAddress: AddressFormValues = {
  address1: "",
  cityName: "",
  stateName: "",
  district: "",
  postCode: "",
  phone: "",
};

const addressSchema = yup.object().shape({
  address1: yup.string().trim().required("Address line is required"),
  cityName: yup.string().trim().required("City is required"),
  phone: yup.string().trim().required("Phone number is required"),
});

const AddressFormDialog: React.FC<AddressFormDialogProps> = ({
  open,
  userId,
  address,
  onClose,
  onSaved,
  onError,
  onSuccess,
}) => {
  const isEditMode = !!address?.addressId;
  const [initialValues, setInitialValues] = useState<AddressFormValues>(emptyAddress);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInitialValues({
      address1: address?.address1 || "",
      cityName: address?.cityName || "",
      stateName: address?.stateName || "",
      district: address?.district || "",
      postCode: address?.postCode || "",
      phone: address?.phone || "",
    });
  }, [open, address]);

  const handleSubmit = (values: AddressFormValues, { setSubmitting }: any) => {
    if (!userId) return;
    setSaving(true);
    const request = isEditMode
      ? AddressService.updateAddress(address!.addressId, { ...values, userId })
      : AddressService.addAddress({ ...values, userId });

    request
      .then(() => {
        onSuccess(isEditMode ? "Address updated successfully." : "Address added successfully.");
        onSaved();
        onClose();
      })
      .catch((err: any) => {
        onError(getErrorMessage(err, "Could not save this address. Please try again."));
      })
      .finally(() => {
        setSaving(false);
        setSubmitting(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={addressSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit: submit, isSubmitting }) => (
          <form onSubmit={submit}>
            <DialogTitle>{isEditMode ? "Edit Address" : "Add New Address"}</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="address1"
                  value={values.address1}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.address1 && !!errors.address1}
                  helperText={touched.address1 && errors.address1}
                />
                <TextField
                  fullWidth
                  label="City"
                  name="cityName"
                  value={values.cityName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.cityName && !!errors.cityName}
                  helperText={touched.cityName && errors.cityName}
                />
                <TextField
                  fullWidth
                  label="State"
                  name="stateName"
                  value={values.stateName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="District"
                  name="district"
                  value={values.district}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Post Code"
                  name="postCode"
                  value={values.postCode}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={values.phone}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={!!touched.phone && !!errors.phone}
                  helperText={touched.phone && errors.phone}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting || saving}>
                {saving ? <CircularProgress size={20} color="inherit" /> : "Save Address"}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddressFormDialog;
